<script lang="ts" setup>
import { TicketCart } from '@/@types'
import { Ticket } from '@/@types/workshop'
import { Counter, CounterDecrement } from '@/components/ui/counter'
import CounterIncrement from '@/components/ui/counter/CounterIncrement.vue'
import CounterValue from '@/components/ui/counter/CounterValue.vue'
import { Title } from '@/components/ui/title'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { assetCategory } from '@/helpers/check-assets'
import { currentPrice, currentPriceFormat, formatPrice, shouldShowPromotion } from '@/helpers/current-price'
import { formatDateFull } from '@/helpers/format-date'
import { findTicketLot } from '@/helpers/ticket-batch'
import { Check, CircleHelp } from 'lucide-vue-next'

import { ref } from 'vue'

const props = defineProps<{
    ticket: Ticket
}>()

const emits = defineEmits<{
    (e: 'updateTotal', payload: { price: number; type: 'increment' | 'decrement' | 'direct'; ticket: TicketCart; currentTotal: number }): void
}>()

const count = ref(0)
const oldCount = ref(0)

const increment = () => {
    oldCount.value = count.value

    count.value++

    const ticket = {
        ticket_id: props.ticket.id,
        quantity: count.value,
    } satisfies TicketCart

    emits('updateTotal', { price: currentPrice(props.ticket), type: 'increment', ticket, currentTotal: count.value })
}
const decrement = () => {
    if (count.value === 0) return
    oldCount.value = count.value

    count.value--

    const ticket = {
        ticket_id: props.ticket.id,
        quantity: count.value,
    } satisfies TicketCart

    emits('updateTotal', { price: currentPrice(props.ticket), type: 'decrement', ticket, currentTotal: count.value })
}

function handleCounterInput() {
    if (!count.value) count.value = 0

    oldCount.value = count.value

    const ticket = {
        ticket_id: props.ticket.id,
        quantity: count.value,
    } satisfies TicketCart

    emits('updateTotal', { price: currentPrice(props.ticket), type: 'direct', ticket, currentTotal: count.value })
}
</script>

<template>
    <div class="group relative overflow-hidden rounded-[10px] p-px">
        <div
            class="after:bg-linear paused group-hover:after:animate-rotate-rainbow after:to-primary absolute inset-0 after:absolute after:top-1/2 after:left-1/2 after:aspect-square after:min-h-[200%] after:min-w-[200%] after:-translate-x-1/2 after:-translate-y-1/2 after:bg-linear-60 after:from-transparent after:from-35% after:to-100% after:content-['']"
        ></div>
        <div class="relative z-10 flex h-full flex-1 flex-col justify-between gap-4 rounded-[10px] bg-[#131518] p-4 md:gap-8 md:p-6">
            <div class="bg-primary font-inter absolute top-0 right-0 rounded-bl-[10px] px-6 py-1 font-semibold">
                {{ $t('Batch') }} {{ findTicketLot(props.ticket.promotions) + 1 }}
            </div>
            <div class="flex flex-col gap-4 md:gap-8">
                <div>
                    <h3 class="mt-4 text-lg font-semibold">{{ $t(ticket.category) }}</h3>
                    <time class="mt-3" :datetime="new Date(ticket.promotions[findTicketLot(props.ticket.promotions)].end_date).toISOString()">
                        {{ $t('Batch available at') }}:
                        <strong>{{ formatDateFull(ticket.promotions[findTicketLot(props.ticket.promotions)].end_date, $i18n.locale) }}</strong>
                    </time>
                </div>
                <div class="flex flex-col">
                    <small class="text-lg font-medium" v-if="shouldShowPromotion(ticket)">
                        {{ $t('From') }}
                        <span class="line-through">
                            {{ formatPrice(Number((shouldShowPromotion(ticket) || 0) / 100)) }}
                        </span>
                    </small>
                    <div>
                        <Title class="flex items-center gap-2 font-semibold">
                            <span v-show="shouldShowPromotion(ticket)">
                                {{ $t('For') }}
                            </span>
                            <data :value="currentPriceFormat(ticket)">{{ currentPriceFormat(ticket) }}</data>
                        </Title>

                        <div class="flex items-center gap-1 text-sm">
                            {{ $t('For') }}
                            3x
                            {{ currentPriceFormat(ticket, 3) }}
                            {{ $t('Without Interest') }}
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger aria-label="{{ $t('Valid for payments via credit card, except purchases via PayPal') }}">
                                        <CircleHelp class="size-4" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <div class="max-w-64 text-center">
                                            {{ $t('Valid for payments via credit card, except purchases via PayPal') }}
                                        </div>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    </div>
                </div>

                <ul class="space-y-4">
                    <li
                        v-for="(asset, index) in assetCategory(ticket.category).benefits"
                        :key="`asset-${index}`"
                        class="flex items-center gap-2 text-sm"
                    >
                        <span
                            class="bg-primary flex h-6 min-w-6 items-center justify-center self-start rounded-full border border-slate-800 text-white"
                        >
                            <Check class="size-4 text-black" />
                        </span>
                        {{ $t(asset) }}
                    </li>
                </ul>
            </div>

            <Counter :min="0" :max="1500" :counter="count" class="mt-4 flex items-center gap-4">
                <CounterDecrement @decrement="decrement" class="text-white" />
                <CounterValue class="text-white" v-model:counter="count" @input="handleCounterInput" />
                <CounterIncrement @increment="increment" class="text-white" />
            </Counter>
        </div>
    </div>
</template>
